import React, {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CheckIcon from '@mui/icons-material/Check';
import { IconSearch, IconX } from '@tabler/icons-react';
import { Popover, Typography } from '@mui/material';

import { AgentProfileResponseDto } from '@shared/services/apiService';

import {
  GROUP_HEADER_HEIGHT,
  POPOVER_MAX_HEIGHT,
  ROW_HEIGHT,
  VIRTUALIZATION_THRESHOLD,
  useStyles,
} from './styles';
import { ProfileGroup, useFilteredProfiles } from './useFilteredProfiles';

interface RolePickerPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  profiles: AgentProfileResponseDto[] | undefined;
  selectedId: string | undefined;
  onClose: () => void;
  onSelect: (profile: AgentProfileResponseDto) => void;
}

type FlatItem =
  | { kind: 'pinned'; profile: AgentProfileResponseDto; height: number }
  | { kind: 'header'; key: string; labelKey: string; height: number }
  | { kind: 'separator'; key: string; height: number }
  | { kind: 'item'; profile: AgentProfileResponseDto; group: ProfileGroup['key']; height: number };

const SEPARATOR_HEIGHT = 9;

const buildFlatList = (
  pinnedNoRole: AgentProfileResponseDto | null,
  groups: ProfileGroup[],
): FlatItem[] => {
  const items: FlatItem[] = [];
  if (pinnedNoRole) {
    items.push({ kind: 'pinned', profile: pinnedNoRole, height: ROW_HEIGHT });
    if (groups.length > 0) {
      items.push({ kind: 'separator', key: 'sep-pinned', height: SEPARATOR_HEIGHT });
    }
  }
  for (const group of groups) {
    items.push({
      kind: 'header',
      key: `header-${group.key}`,
      labelKey: group.labelKey,
      height: GROUP_HEADER_HEIGHT,
    });
    for (const profile of group.items) {
      items.push({ kind: 'item', profile, group: group.key, height: ROW_HEIGHT });
    }
  }
  return items;
};

const isSelectableItem = (item: FlatItem): item is Extract<FlatItem, { kind: 'pinned' | 'item' }> =>
  item.kind === 'pinned' || item.kind === 'item';

const getTypeChipKey = (
  type: AgentProfileResponseDto['type'] | undefined,
): 'system' | 'custom' | 'noRole' => {
  if (!type) return 'noRole';
  if (type === 'system') return 'system';
  return 'custom';
};

const truncatePrompt = (prompt: string | undefined | null, maxChars = 80): string => {
  if (!prompt) return '';
  const cleaned = prompt.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= maxChars) return cleaned;
  return `${cleaned.slice(0, maxChars).trimEnd()}…`;
};

export const RolePickerPopover: React.FC<RolePickerPopoverProps> = ({
  open,
  anchorEl,
  profiles,
  selectedId,
  onClose,
  onSelect,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(POPOVER_MAX_HEIGHT);

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

  const filtered = useFilteredProfiles(profiles, query);

  const flatItems = useMemo(
    () => buildFlatList(filtered.pinnedNoRole, filtered.groups),
    [filtered.pinnedNoRole, filtered.groups],
  );

  const selectableIndices = useMemo(() => {
    const indices: number[] = [];
    flatItems.forEach((item, idx) => {
      if (isSelectableItem(item)) indices.push(idx);
    });
    return indices;
  }, [flatItems]);

  // Cumulative offsets for virtualized positioning (and consistent layout overall).
  const offsets = useMemo(() => {
    const result: number[] = [];
    let acc = 0;
    for (const item of flatItems) {
      result.push(acc);
      acc += item.height;
    }
    result.push(acc);
    return result;
  }, [flatItems]);

  const totalHeight = offsets[offsets.length - 1] ?? 0;

  // Reset state when popover opens or query changes the first selectable target.
  useEffect(() => {
    if (!open) return;
    setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (selectableIndices.length === 0) {
      setActiveIndex(-1);
      return;
    }
    // Prefer the currently selected profile if visible.
    if (selectedId) {
      const selectedFlatIndex = flatItems.findIndex(
        (item) => isSelectableItem(item) && item.profile.id === selectedId,
      );
      if (selectedFlatIndex !== -1) {
        setActiveIndex(selectedFlatIndex);
        return;
      }
    }
    setActiveIndex(selectableIndices[0]);
  }, [open, query, flatItems, selectableIndices, selectedId]);

  // Focus the search input on open (the Popover gets focus first; we steal it).
  useLayoutEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Track viewport height for virtualization decisions.
  useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    setViewportHeight(list.clientHeight);
  }, [open, flatItems.length]);

  // Scroll the active item into view (header-aware).
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const itemTop = offsets[activeIndex] ?? 0;
    const itemBottom = itemTop + (flatItems[activeIndex]?.height ?? ROW_HEIGHT);
    const visibleTop = list.scrollTop + GROUP_HEADER_HEIGHT;
    const visibleBottom = list.scrollTop + list.clientHeight;
    if (itemTop < visibleTop) {
      list.scrollTop = Math.max(0, itemTop - GROUP_HEADER_HEIGHT);
    } else if (itemBottom > visibleBottom) {
      list.scrollTop = itemBottom - list.clientHeight;
    }
  }, [activeIndex, open, offsets, flatItems]);

  const moveActive = useCallback(
    (direction: 1 | -1) => {
      if (selectableIndices.length === 0) return;
      const currentPos = selectableIndices.indexOf(activeIndex);
      const nextPos =
        currentPos === -1
          ? direction === 1
            ? 0
            : selectableIndices.length - 1
          : (currentPos + direction + selectableIndices.length) % selectableIndices.length;
      setActiveIndex(selectableIndices[nextPos]);
    },
    [activeIndex, selectableIndices],
  );

  const handleSelectFlatIndex = useCallback(
    (index: number) => {
      const item = flatItems[index];
      if (!item || !isSelectableItem(item)) return;
      onSelect(item.profile);
      onClose();
    },
    [flatItems, onSelect, onClose],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveActive(1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(-1);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (activeIndex >= 0) handleSelectFlatIndex(activeIndex);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === 'Home') {
        event.preventDefault();
        if (selectableIndices.length > 0) setActiveIndex(selectableIndices[0]);
        return;
      }
      if (event.key === 'End') {
        event.preventDefault();
        if (selectableIndices.length > 0) {
          setActiveIndex(selectableIndices[selectableIndices.length - 1]);
        }
        return;
      }
    },
    [moveActive, activeIndex, handleSelectFlatIndex, onClose, selectableIndices],
  );

  const handleListScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  const handleClearQuery = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setQuery('');
    searchInputRef.current?.focus();
  }, []);

  const isVirtualized = flatItems.length > VIRTUALIZATION_THRESHOLD;

  const visibleRange = useMemo(() => {
    if (!isVirtualized) return { start: 0, end: flatItems.length };
    const buffer = 6;
    let start = 0;
    while (start < flatItems.length && offsets[start + 1] <= scrollTop) start += 1;
    start = Math.max(0, start - buffer);
    let end = start;
    const limit = scrollTop + viewportHeight;
    while (end < flatItems.length && offsets[end] < limit) end += 1;
    end = Math.min(flatItems.length, end + buffer);
    return { start, end };
  }, [isVirtualized, flatItems, offsets, scrollTop, viewportHeight]);

  const renderItem = (item: FlatItem, index: number) => {
    if (item.kind === 'separator') {
      return <div key={item.key} className={classes.pinnedSeparator} role="presentation" />;
    }

    if (item.kind === 'header') {
      return (
        <div key={item.key} className={classes.groupHeader} role="presentation" aria-hidden="true">
          {t(item.labelKey)}
        </div>
      );
    }

    const profile = item.profile;
    const isSelected = !!selectedId && profile.id === selectedId;
    const isActive = index === activeIndex;
    const chipKey = item.kind === 'pinned' ? 'noRole' : getTypeChipKey(profile.type);
    const chipLabel =
      chipKey === 'system'
        ? t('campaignPage.profilePicker.typeBadgeSystem')
        : chipKey === 'custom'
          ? t('campaignPage.profilePicker.typeBadgeCustom')
          : t('campaignPage.profilePicker.typeBadgeNoProfile');

    const promptPreview =
      item.kind === 'pinned'
        ? t('campaignPage.profilePicker.noProfileDescription')
        : truncatePrompt(profile.prompt);

    const optionId = `role-picker-option-${profile.id}`;

    return (
      <button
        key={profile.id || `pinned-${item.kind}`}
        type="button"
        ref={(node) => {
          if (node) itemRefs.current.set(index, node);
          else itemRefs.current.delete(index);
        }}
        id={optionId}
        role="option"
        aria-selected={isSelected}
        tabIndex={-1}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => handleSelectFlatIndex(index)}
        className={classNames(classes.row, {
          [classes.rowActive]: isActive && !isSelected,
          [classes.rowSelected]: isSelected,
        })}>
        <div className={classes.rowBody}>
          <span className={classes.rowName}>{profile.name}</span>
          {promptPreview && <span className={classes.rowPrompt}>{promptPreview}</span>}
        </div>
        <div className={classes.rowMeta}>
          {!isSelected && (
            <span
              className={classNames(classes.typeChip, {
                [classes.typeChipSystem]: chipKey === 'system',
                [classes.typeChipCustom]: chipKey === 'custom',
                [classes.typeChipNoRole]: chipKey === 'noRole',
              })}>
              {chipLabel}
            </span>
          )}
          {isSelected && (
            <span className={classes.checkIcon} aria-hidden="true">
              <CheckIcon fontSize="inherit" />
            </span>
          )}
        </div>
      </button>
    );
  };

  const activeDescendant =
    activeIndex >= 0 && isSelectableItem(flatItems[activeIndex] ?? ({} as FlatItem))
      ? `role-picker-option-${(flatItems[activeIndex] as Extract<FlatItem, { kind: 'pinned' | 'item' }>).profile.id}`
      : undefined;

  const renderedItems = flatItems.slice(visibleRange.start, visibleRange.end);
  const topSpacer = isVirtualized ? (offsets[visibleRange.start] ?? 0) : 0;
  const bottomSpacer = isVirtualized ? totalHeight - (offsets[visibleRange.end] ?? totalHeight) : 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { className: classes.popoverPaper } }}
      onKeyDown={handleKeyDown}>
      <div className={classes.searchHeader}>
        <span className={classes.searchIcon} aria-hidden="true">
          <IconSearch size={16} />
        </span>
        <input
          ref={searchInputRef}
          type="text"
          dir="auto"
          role="combobox"
          aria-expanded={open}
          aria-controls="role-picker-listbox"
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          className={classes.searchInput}
          placeholder={t('campaignPage.profilePicker.searchPlaceholder') as string}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button
            type="button"
            className={classes.searchClear}
            aria-label={t('campaignPage.profilePicker.clearSearch') as string}
            onClick={handleClearQuery}>
            <IconX size={14} />
          </button>
        )}
      </div>

      <div
        id="role-picker-listbox"
        role="listbox"
        ref={listRef}
        className={classes.list}
        onScroll={handleListScroll}>
        {filtered.hasResults ? (
          <>
            {topSpacer > 0 && (
              <div className={classes.virtualSpacer} style={{ height: topSpacer }} />
            )}
            {renderedItems.map((item, localIdx) => renderItem(item, visibleRange.start + localIdx))}
            {bottomSpacer > 0 && (
              <div className={classes.virtualSpacer} style={{ height: bottomSpacer }} />
            )}
          </>
        ) : (
          <div className={classes.emptyState}>
            <Typography component="span" sx={{ fontSize: 13 }}>
              {t('campaignPage.profilePicker.empty', { query })}
            </Typography>
            <a
              href="/admin/assistants"
              target="_blank"
              rel="noreferrer noopener"
              className={classes.emptyStateAction}>
              {t('campaignPage.profilePicker.createNew')}
            </a>
          </div>
        )}
      </div>
    </Popover>
  );
};
